import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

const STORAGE_KEY = "bullen.myEntry";

export type Settings = {
	/** FPL entry id of the viewer's own team, or null when not chosen. */
	myEntryId: number | null;
	setMyEntryId: (id: number | null) => void;
};

const readStored = (): number | null => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		const id = raw == null ? NaN : Number(raw);
		return Number.isFinite(id) ? id : null;
	} catch {
		return null;
	}
};

const writeStored = (id: number | null) => {
	try {
		if (id == null) {
			window.localStorage.removeItem(STORAGE_KEY);
		} else {
			window.localStorage.setItem(STORAGE_KEY, String(id));
		}
	} catch {
		// storage can be unavailable in private windows; the choice just will not persist
	}
};

const SettingsContext = createContext<Settings | null>(null);

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
	const [myEntryId, setState] = useState<number | null>(readStored);

	const setMyEntryId = useCallback((id: number | null) => {
		writeStored(id);
		setState(id);
	}, []);

	const value = useMemo(() => ({ myEntryId, setMyEntryId }), [myEntryId, setMyEntryId]);
	return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): Settings => {
	const settings = useContext(SettingsContext);
	if (settings == null) {
		throw new Error("useSettings must be used inside a SettingsProvider");
	}
	return settings;
};
